import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import * as notificationApi from '../lib/api/notifications'

export interface UseNotificationPollingOptions {
  interval?: number // Polling interval in milliseconds
  enabled?: boolean // Enable/disable polling
  onNewNotifications?: (count: number) => void // Callback when count changes
}

export interface UseNotificationPollingReturn {
  unreadCount: number | undefined
  isPolling: boolean
  error: Error | null
  startPolling: () => void
  stopPolling: () => void
  checkNow: () => Promise<void>
}

export function useNotificationPolling(
  options: UseNotificationPollingOptions = {},
): UseNotificationPollingReturn {
  const { currentWorkspace } = useWorkspace()
  const { user } = useAuth()
  const [unreadCount, setUnreadCount] = useState<number | undefined>(undefined)
  const [isPolling, setIsPolling] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const interval = options.interval ?? 15000 // Default 15 seconds for lightweight polling
  const enabled = options.enabled ?? true
  const onNewNotifications = options.onNewNotifications

  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isActiveRef = useRef(true)
  const isPollingRef = useRef(false)
  const lastCountRef = useRef(0)
  const retryCountRef = useRef(0)
  const maxRetries = 3

  // Check for new notifications
  const checkNow = useCallback(async () => {
    if (!user)
      return

    try {
      const response = await notificationApi.getUnreadCount(currentWorkspace?.id)
      const newCount = response.count

      // Reset retry count on success
      retryCountRef.current = 0
      setError(null)

      // Check if count has increased (new notifications)
      if (newCount > lastCountRef.current && lastCountRef.current > 0) {
        onNewNotifications?.(newCount - lastCountRef.current)
      }

      lastCountRef.current = newCount
      setUnreadCount(newCount)
    }
    catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch unread count')
      setError(error)
      console.error('Error fetching unread count:', error)

      // Implement exponential backoff for retries
      retryCountRef.current++
      if (retryCountRef.current >= maxRetries) {
        stopPolling()
        console.error('Max retries reached, stopping polling')
      }
    }
  }, [user, currentWorkspace?.id, onNewNotifications])

  // Schedule next poll with exponential backoff
  const scheduleNextPoll = useCallback(() => {
    if (!isActiveRef.current || !user || !isPollingRef.current)
      return

    // Calculate delay with exponential backoff
    const delay = enabled ? interval * 2 ** retryCountRef.current : 0

    if (delay > 0) {
      pollingTimeoutRef.current = setTimeout(async () => {
        if (isActiveRef.current && isPollingRef.current) {
          await checkNow()
          scheduleNextPoll() // Recursive scheduling
        }
      }, delay)
    }
  }, [user, interval, enabled, checkNow])

  // Start polling
  const startPolling = useCallback(() => {
    if (!user || pollingTimeoutRef.current)
      return

    isPollingRef.current = true
    setIsPolling(true)
    retryCountRef.current = 0

    // Initial check
    checkNow().then(() => {
      // Schedule next poll after initial check
      scheduleNextPoll()
    })
  }, [user, checkNow, scheduleNextPoll])

  // Stop polling
  const stopPolling = useCallback(() => {
    isPollingRef.current = false
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current)
      pollingTimeoutRef.current = null
    }
    setIsPolling(false)
  }, [])

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      isActiveRef.current = !document.hidden

      // When page becomes visible again, do an immediate check
      if (!document.hidden && isPolling) {
        checkNow()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isPolling, checkNow])

  // Handle focus/blur for additional optimization
  useEffect(() => {
    const handleFocus = () => {
      isActiveRef.current = true
      if (isPolling) {
        checkNow()
      }
    }

    const handleBlur = () => {
      isActiveRef.current = false
    }

    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)

    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [isPolling, checkNow])

  // Handle workspace changes - restart polling when workspace changes
  useEffect(() => {
    if (isPolling) {
      stopPolling()
      lastCountRef.current = 0 // Reset unread-count delta to avoid false toasts
      startPolling()
    }
  }, [currentWorkspace?.id]) // Restart polling when workspace changes

  // Auto-start polling if enabled
  useEffect(() => {
    if (enabled && user && !pollingTimeoutRef.current) {
      startPolling()
    }
    else if (!enabled || !user) {
      stopPolling()
      // Clear unread count when user logs out
      if (!user) {
        setUnreadCount(undefined)
        lastCountRef.current = 0
      }
    }

    return () => {
      stopPolling()
    }
  }, [enabled, user]) // Intentionally not including startPolling/stopPolling to avoid loops

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current)
      }
    }
  }, [])

  return {
    unreadCount,
    isPolling,
    error,
    startPolling,
    stopPolling,
    checkNow,
  }
}
